import {
  OnboardingGuide,
  type OnboardingGuideProps
} from "@prismicio/editor-fields"
import { ActionListItem } from "@prismicio/editor-ui"
import type { ReactNode } from "react"
import { useNavigate } from "react-router-dom"

import { getRepositoryName } from "../../app/routing"
import { useRepository } from "../../app/services/repository"
import { useSharedOnboarding } from "../../app/services/sharedOnboarding"
import { trackSharedOnboardingGuide } from "../../app/services/tracking"
import type { OnboardingStep as PageBuilderOnboardingStep } from "../Onboarding/Onboarding"
import { useShouldDisplayOnboarding as useShouldPBDisplayOnboarding } from "../Onboarding/useShouldDisplayOnboarding"
import { useSharedOnboardingParams } from "./useSharedOnboardingParams"

interface DocumentListSharedOnboardingProps {
  fallback?: ReactNode
}

export function DocumentListSharedOnboarding(
  props: DocumentListSharedOnboardingProps
) {
  const { fallback } = props
  const { onboarding, toggleStep, toggleGuide } = useSharedOnboarding()
  const stepOverrides = useStepOverrides()

  if (!onboarding) return null

  if (onboarding.isDismissed) {
    return (
      <ActionListItem
        textVariant="normal"
        backgroundColor="transparent"
        startIcon="notStarted"
        onClick={toggleGuide}
      >
        Get started
      </ActionListItem>
    )
  }

  const repositoryName = getRepositoryName()

  return (
    <OnboardingGuide
      tracking={{ track: trackSharedOnboardingGuide, source: "PageBuilder" }}
      onboardingState={onboarding}
      onToggleGuide={toggleGuide}
      onToggleStep={toggleStep}
      fallback={fallback}
      stepOverrides={stepOverrides}
      projectName={repositoryName}
      repositoryName={repositoryName}
    />
  )
}

function useStepOverrides(): OnboardingGuideProps["stepOverrides"] | undefined {
  const navigate = useNavigate()

  const { starterId } = useRepository()
  const isPBOnboardingEnabled = useShouldPBDisplayOnboarding()
  const { searchParams, setStepParam } = useSharedOnboardingParams()

  if (!isPBOnboardingEnabled) return undefined

  function goToPageBuilderStep(step: PageBuilderOnboardingStep) {
    setStepParam(step)
    navigate(`/working?${searchParams.toString()}`)
  }

  if (starterId) {
    return {
      createProject: () => goToPageBuilderStep("launchStarter"),
      setupSliceMachine: () => goToPageBuilderStep("launchStarter")
    }
  }

  return {
    createProject: () => goToPageBuilderStep("createProject"),
    setupSliceMachine: () => goToPageBuilderStep("setupSliceMachine")
  }
}
